import { createSignal, For, JSXElement } from 'solid-js';
import { useIntl } from '@cookbook/solid-intl';
import { useToast } from '~/components/ui/Toast';
import { useFSAModalContext } from '~/pages/FSAOnboarding/FSAModalContext.tsx';
import Modal from '~/components/ui/Modal';
import { ModalSizes, ModalTypes } from '~/components/ui/Modal/Modal.tsx';
import { Localized } from '~/i18n';
import {
  disableFSAWithSuccessToast,
  FSA_DEACTIVATE_FEATURES_TRANSLATION_KEYS,
  getTranslationKey
} from '~/pages/FSAOnboarding/utils.tsx';
import { CircleXIcon } from 'lucide-solid';
import { Button } from '~/components/ui/Button';
import logger from '~/utils/logger';
import { getLocalizedString } from '~/i18n/utils.ts';

export default function FSADeactivateWarningModal(): JSXElement {
  const intl = useIntl();
  const toaster = useToast();
  const fsaModalContext = useFSAModalContext();
  const [isLoading, setIsLoading] = createSignal<boolean>(false);

  return (
    <Modal
      open={fsaModalContext.isFSADeactivateWarningModalOpen}
      size={ModalSizes.LARGE}
      type={ModalTypes.DIALOG}
    >
      {(closeModal) => (
        <>
          <div class={`flex gap-6`}>
            <div class={`flex flex-col justify-between grow`}>
              <div class={`flex flex-col gap-5`}>
                <h2 class={`text-md font-semibold`}>
                  <Localized
                    translationKey={getTranslationKey(`fsa_disable_header`)}
                  />
                </h2>
                <p class={`text-sm`}>
                  <Localized
                    translationKey={getTranslationKey(`fsa_disable_subheader`)}
                  />
                </p>
                <ul class={`flex flex-col gap-5`}>
                  <For each={FSA_DEACTIVATE_FEATURES_TRANSLATION_KEYS}>
                    {(translationKey) => (
                      <li class={`pl-8 relative flex items-center`}>
                        <div class={`absolute left-0`}>
                          <CircleXIcon
                            width={24}
                            strokeWidth={1}
                            class={`text-[#E5484D]`}
                          />
                        </div>
                        <Localized translationKey={translationKey} />
                      </li>
                    )}
                  </For>
                </ul>
              </div>
              <div class={`flex justify-end gap-4 mt-6`}>
                <Button
                  onClick={() => {
                    closeModal();
                    fsaModalContext.hideFSADeactivateWarningModal();
                  }}
                  variant={`outline`}
                  size={'sm'}
                  data-testid={'fsa-banner-cancel'}
                >
                  <Localized translationKey={'cta_labels.cancel'} />
                </Button>
                <Button
                  variant={`solid`}
                  size={'sm'}
                  color={'danger'}
                  loading={isLoading()}
                  data-testid={'fsa-banner-disable'}
                  onClick={async () => {
                    try {
                      setIsLoading(true);
                      await disableFSAWithSuccessToast({
                        toaster,
                        intl
                      });
                    } catch (error) {
                      logger.error(error);
                      toaster.toast().error({
                        description: getLocalizedString(
                          getTranslationKey('disable_fsa_failure'),
                          {
                            intl
                          }
                        )
                      });
                    } finally {
                      setIsLoading(false);
                      closeModal();
                      fsaModalContext.hideFSADeactivateWarningModal();
                    }
                  }}
                >
                  <Localized
                    translationKey={'cta_labels.disable_full_stack_auth'}
                  />
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </Modal>
  );
}
